import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import strings from 'strings';
import { Loader, MessageBlock } from '@getro/rombo';
import { Box } from 'rebass/styled-components';
import { useGetIndustryQuery } from 'services/organizationsV2';
import { useCompanyLayoutName } from 'hooks/useCompanyLayoutName';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import CareersPages from './CareersPages';
import Info from './Info';
import Settings from './Settings';
import { loadRemoveCompany, loadTopicOptions, resetCompanyProfile } from './actions';
import { makeSelectIsLoading } from '../../redux/loadingSelector';
import { key, makeSelectCompanySettings } from './selectors';
import { useInjectReducer } from '../../hooks/useInjectReducer';
import reducer from './reducer';
import { useInjectSaga } from '../../hooks/useInjectSaga';
import saga from './saga';
import usePermissions from '../../hooks/usePermissions';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';
import BannerLandingPage from './bannerLandingPage';
import { CompanyProfileLayout } from '../../components/layout/CompanyProfileLayout';
import useCompany from '../../hooks/useCompany';
import usePageHook from '../../hooks/usePageHook';

const pageTitle = '';

const CompanyProfile = () => {
  const history = useHistory();
  useInjectReducer({ key, reducer });
  useInjectSaga({ key, saga });
  const { company, initialized, error, companySlug, updateCompany, isUpdating, updateCompanyError, resetError } =
    useCompany();
  const dispatch = useDispatch();
  const permissions = usePermissions({ companySlug });
  const activeNetwork = useSelector(activeNetworkSelector);
  const currentUser = useSelector(userSelector);
  const companySettings = useSelector(makeSelectCompanySettings);
  const isRemoveCompanyLoading = useSelector(makeSelectIsLoading('removeCompany'));
  useGetIndustryQuery();
  usePageHook('Company profile');

  useEffect(
    () => () => {
      dispatch(resetCompanyProfile());
      resetError();
    },
    [dispatch, resetError],
  );

  useEffect(() => {
    dispatch(
      loadTopicOptions({
        networkId: activeNetwork.id,
      }),
    );
  }, [activeNetwork.id, dispatch]);

  const { isCompanyProfile, isInCompanyProfileOrNetwork } = useCompanyLayoutName(initialized);

  return (
    <MainLayout>
      {error && (
        <>
          <MainLayoutHeader title={company?.name ?? pageTitle} />
          <Box mt="24px" maxWidth="400px" mx="auto">
            {error !== 404 && <MessageBlock type="error" content={strings.genericError} />}
            {error === 404 && (
              <MessageBlock
                type="warning"
                title="Company not found"
                content="It looks like the URL is not correct or the company doesn’t belong to the active network."
              />
            )}
          </Box>
        </>
      )}

      {!error && !initialized && (
        <>
          <MainLayoutHeader title={company?.name ?? pageTitle} />
          <Loader />
        </>
      )}
      {!error && initialized && (
        <>
          <CompanyProfileLayout
            isCompanyNetwork={activeNetwork.isCompany}
            description={strings.companyProfile.description}
            showNotPublishedInfo={!company.qa}
            isInCompanyProfileOrNetwork={isInCompanyProfileOrNetwork}
          >
            <BannerLandingPage mb="4" activeNetwork={activeNetwork} slug={companySlug} />
            <Info
              company={company}
              onCompanyUpdate={updateCompany}
              error={updateCompanyError}
              editable={permissions.company.canManageGeneralInformation}
              updating={isUpdating}
              network={activeNetwork}
            />

            <CareersPages activeNetwork={activeNetwork} currentUser={currentUser} company={company} />
            {!isCompanyProfile && (
              <Settings
                history={history}
                companySettings={companySettings}
                isRemoveCompanyLoading={isRemoveCompanyLoading}
                removeCompany={(params) => dispatch(loadRemoveCompany(params))}
                network={activeNetwork}
                company={company}
              />
            )}
          </CompanyProfileLayout>
        </>
      )}
    </MainLayout>
  );
};

export default CompanyProfile;
